import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { gt } from 'ember-truth-helpers';
import EmptyState from 'garaje/components/empty-state';
import QuestionnaireSelectorModal from 'garaje/components/flows/sections/questionnaires/selector-modal';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import UiCard from 'garaje/components/ui/card';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiWell from 'garaje/components/ui/well';
import UiWellItem from 'garaje/components/ui/well/item';
import type Company from 'garaje/models/company';
import type Flow from 'garaje/models/flow';
import type FlowQuestionnaire from 'garaje/models/flow-questionnaire';
import type GlobalFlow from 'garaje/models/global-flow';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import type FlashMessagesService from 'garaje/services/flash-messages';
import _sortBy from 'lodash/sortBy';

interface FlowsSectionsQuestionnairesSignature {
  Args: {
    canCreateQuestionnaires: boolean;
    createQuestionnaireRoute: string;
    company?: Company;
    isDisabled: boolean;
    flow: Flow | GlobalFlow;
    location?: Location;
    /**
     * Text to be shown as the title of an <EmptyState> when `@questionnairesCount === 0`
     */
    noQuestionnairesMessage: string;
    questionnaireCount: number;
    viewQuestionnaireRoute: string;
  };
  Element: HTMLDivElement;
}

export default class FlowsSectionsQuestionnaires extends Component<FlowsSectionsQuestionnairesSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;

  @tracked showingSelectorModal = false;

  @tracked _flowQuestionnaireToRemove: FlowQuestionnaire | null = null;

  get flowQuestionnaires(): FlowQuestionnaire[] {
    return <FlowQuestionnaire[]>_sortBy(this.args.flow.flowQuestionnaires, 'position');
  }

  get isShowingRemoveConfirmationModal(): boolean {
    return !!this._flowQuestionnaireToRemove;
  }

  get questionnaires(): Questionnaire[] {
    return this.flowQuestionnaires.map((fq) => fq.questionnaire);
  }

  get showAddButton(): boolean {
    return !this.args.isDisabled && !this.thereAreNoQuestionnaires;
  }

  get thereAreNoQuestionnaires(): boolean {
    return this.args.questionnaireCount === 0;
  }

  @action
  async addQuestionnaires(questionnaires: Questionnaire[]): Promise<void> {
    try {
      let highestPosition = Math.max(...this.args.flow.flowQuestionnaires.map((fq) => fq.position));
      const promises = questionnaires.map((questionnaire) => {
        const flowQuestionnaire = this.store.createRecord('flow-questionnaire', {
          flow: this.args.flow,
          questionnaire,
          position: ++highestPosition,
        });
        return flowQuestionnaire.save();
      });
      await Promise.all(promises);
      this.hideSelectorModal();
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', 'Something went wrong', { details: 'Please try again.' });
    }
  }

  @action
  cancelRemove(): void {
    this._flowQuestionnaireToRemove = null;
  }

  @action
  confirmRemoveQuestionnaire(flowQuestionnaire: FlowQuestionnaire): void {
    this._flowQuestionnaireToRemove = flowQuestionnaire;
  }

  @action
  hideSelectorModal(): void {
    this.showingSelectorModal = false;
  }

  @action
  removeQuestionnaire(): void {
    if (!this._flowQuestionnaireToRemove) return;

    this._flowQuestionnaireToRemove.destroyRecord();
    this._flowQuestionnaireToRemove = null;
  }

  @action
  async reorderQuestionnaires(flowQuestionnairesInNewOrder: FlowQuestionnaire[]): Promise<void> {
    flowQuestionnairesInNewOrder.forEach((flowQuestionnaire, index) => {
      flowQuestionnaire.position = index + 1;
    });
    await Promise.all(flowQuestionnairesInNewOrder.map((fq) => fq.save()));
  }

  /**
   * Decide whether to show the "global" icon for this questionnaire. This should happen if we're viewing
   * a location flow (not a global one) and the questionnaire is not assigned to the location (meaning it's global).
   */
  @action
  showGlobalIconFor(questionnaire: Questionnaire): boolean {
    // don't show "global" icon when viewing a global flow, since _all_ of the questionnaires will be global
    if (this.args.flow.isGlobal) return false;

    // if we weren't given a `@location`, dont't show "global" icon for any questionnaires
    const location = this.args.location;
    if (!location) return false;

    // otherwise, check see whether the questionnaire belongs to the location; if not, show "global" icon
    return questionnaire.belongsTo('location').id() !== location.id;
  }

  @action
  showSelectorModal(): void {
    this.showingSelectorModal = true;
  }

  /**
   * Decide whether to show the "View"/"Edit" button for this questionnaire. This should happen unless we're
   * viewing a location flow and the questionnaire is global.
   */
  @action
  showViewButtonFor(questionnaire: Questionnaire): boolean {
    // always show the button when viewing a global flow
    if (this.args.flow.isGlobal) return true;

    // always show the button if `@location` wasn't given (assume we're on a global view)
    const location = this.args.location;
    if (!location) return true;

    // otherwise, check see whether the questionnaire belongs to the location; if it does, show the button
    return questionnaire.belongsTo('location').id() === location.id;
  }

  <template>
    <SettingsPanel @isExpandable={{true}} @isOpen={{true}} class="p-0 bg-carbon-5" ...attributes as |panel|>
      <panel.header
        @iconUri="/assets/images/features/signature.svg"
        @title="Assessments"
        @description="Visitors can read or watch media and answer questions."
        @showCancel={{false}}
        class="p-4"
      />
      <panel.body>
        {{#if this.showAddButton}}
          <div class="relative z-10 -mx-px py-3 shadow-md shadow-gray-50">
            <div class="px-4 flex flex-row-reverse flex-nowrap">
              <button
                type="button"
                class="btn btn--small btn--brandOutline"
                {{on "click" this.showSelectorModal}}
                data-test-add-questionnaire
              >
                Add assessment
              </button>
            </div>
          </div>
        {{/if}}

        <UiWell class="px-12" data-test-flow-questionnaires-list {{sortableGroup onChange=this.reorderQuestionnaires}}>
          {{#if this.thereAreNoQuestionnaires}}
            <EmptyState @title={{@noQuestionnairesMessage}}>
              <:description>
                {{#if @canCreateQuestionnaires}}
                  <LinkTo @route={{@createQuestionnaireRoute}} class="btn btn--brand">
                    Create an assessment
                  </LinkTo>
                {{else}}
                  An administrator will need to create at least one assessment.
                {{/if}}
              </:description>
            </EmptyState>
          {{else}}
            {{#each this.flowQuestionnaires as |flowQuestionnaire|}}
              {{#let flowQuestionnaire.questionnaire as |questionnaire|}}
                <UiWellItem data-test-questionnaire={{questionnaire.id}} {{sortableItem model=flowQuestionnaire}}>
                  <UiCard class="is-dragging:shadow-lg-tall trans-box-shadow">
                    <div class="flex space-x-4 items-center">
                      <div class="flex-auto flex flex-col space-y-2">
                        <div class="flex items-center">
                          <h4 data-test-questionnaire-title>
                            {{questionnaire.title}}
                          </h4>
                          {{#if (this.showGlobalIconFor questionnaire)}}
                            <GlobalFlowIcon @tooltipText="Global assessment" />
                          {{/if}}
                        </div>
                      </div>
                      {{#if (this.showViewButtonFor questionnaire)}}
                        <LinkTo
                          @route={{@viewQuestionnaireRoute}}
                          @model={{questionnaire.id}}
                          class="btn btn--small {{if @isDisabled 'btn--outline' 'btn--brand'}}"
                          data-test-view-questionnaire
                        >
                          {{if @isDisabled "View" "Edit"}}
                        </LinkTo>
                      {{/if}}
                    </div>
                  </UiCard>
                  {{#unless @isDisabled}}
                    {{#let (gt this.questionnaires.length 1) as |isDraggable|}}
                      {{#if isDraggable}}
                        <div class="icon-reSort handle cursor-row-resize hover:cursor-row-resize" {{sortableHandle}} />
                      {{/if}}

                      <button
                        class="btn btn--smaller btn--square btn--hoverFill {{unless isDraggable '!ml-12'}}"
                        type="button"
                        tabindex="-1"
                        {{on "click" (fn this.confirmRemoveQuestionnaire flowQuestionnaire)}}
                        data-test-delete
                      >
                        <span class="icon-trash"></span>
                      </button>
                    {{/let}}
                  {{/unless}}
                </UiWellItem>
              {{/let}}
            {{else}}
              <EmptyState
                @title="This flow has no assessments."
                @description={{unless
                  @isDisabled
                  'Click the "Add assessment" button to assign assessments to this flow.'
                }}
              />
            {{/each}}
          {{/if}}
        </UiWell>

        <p class="-mx-px p-4 pt-0 text-sm mb-0">
          Learn more about adding and configuring
          <a href="http://envoy.help/en/articles/11840176-assessments" target="_blank" rel="noopener">assessments</a>.
        </p>

        {{#if this.showingSelectorModal}}
          <QuestionnaireSelectorModal
            @addQuestionnaires={{this.addQuestionnaires}}
            @close={{this.hideSelectorModal}}
            @company={{@company}}
            @location={{@location}}
            @assignedQuestionnaires={{this.questionnaires}}
            data-test-add-questionnaire-modal
          />
        {{/if}}

        {{#if this.isShowingRemoveConfirmationModal}}
          <ConfirmationModal
            @ctaText="Remove assessment"
            @handleTaskAction={{this.removeQuestionnaire}}
            @onClose={{this.cancelRemove}}
            @title="Remove assessment?"
          >
            <p>
              Visitors will no longer be required to take this assessment when signing in. You can add this assessment
              back later.
            </p>
          </ConfirmationModal>
        {{/if}}
      </panel.body>
    </SettingsPanel>
  </template>
}
